// Dil seçicinin bağlantısı: etkin dil + seçimi kalıcı yapan setter.
//
// i18n.language ham i18next değeridir ve her zaman bir LocaleCode olmayabilir
// (fallback zinciri "tr" gibi bölgesiz bir değer bırakabilir); seçiciye
// vermeden önce kataloğa oturtulur, aksi halde hiçbir düğme basılı görünmez.
//
// setLocale ayrıca sunucuya da yazar (PUT /v1/me/locale): bildirim kopyasının
// dili entity_employee.preferred_locale'den çözülür (notification-router) —
// yalnız localStorage'a yazan eski hâlinde kullanıcı arayüzü İngilizce'ye
// geçse bile bildirimler sonsuza dek Türkçe kalıyordu. Sunucu yazımı
// kullanıcı akışını BLOKLAMAZ: yerel tercih anında uygulanır (kullanıcının
// niyeti kazanır), hata yutulmaz — syncError üzerinden yüzeye çıkar ve
// bir sonraki seçim denemesi sunucu satırını onarır.

import { useCallback, useState, useSyncExternalStore } from "react";

import { isSupportedLocale, storeLocale, type LocaleCode } from "@novemai/i18n-sdk";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope } from "@/api/errors";

import { CUSTOMER_APP_DEFAULT_LOCALE, i18n } from "./index.ts";
import { localeStorage } from "./locale-storage.ts";

function subscribe(onChange: () => void): () => void {
  i18n.on("languageChanged", onChange);
  return () => {
    i18n.off("languageChanged", onChange);
  };
}

function activeLocale(): LocaleCode {
  return isSupportedLocale(i18n.language) ? i18n.language : CUSTOMER_APP_DEFAULT_LOCALE;
}

export interface LocalePreference {
  readonly locale: LocaleCode;
  readonly setLocale: (locale: LocaleCode) => void;
  /**
   * Son sunucu yazımının hatası (yoksa null). Genişletilmiş şekil: yerel
   * uygulama başarılı olsa da sunucu satırı güncellenemediyse kullanıcıya
   * söylenmelidir (bildirim dili eski kalır) — sessizce yutmak, bu hook'un
   * kapattığı defektin kendisiydi. Görünüm `errorMessage(syncError, t)` ile
   * çevirir (uygulamanın mutasyon-hatası ev deseni).
   */
  readonly syncError: unknown;
}

export function useLocalePreference(): LocalePreference {
  // useSyncExternalStore: dil i18next tarafından da değiştirilebiliyor
  // (useApplyResolvedLocale claim'i uygulayınca). Yerel state tutsaydık
  // seçici o değişimi kaçırır ve yanlış düğmeyi basılı gösterirdi.
  const locale = useSyncExternalStore(subscribe, activeLocale, activeLocale);
  const [syncError, setSyncError] = useState<unknown>(null);

  const setLocale = useCallback((next: LocaleCode) => {
    // Önce sakla: changeLanguage senkron değil, araya giren bir /v1/me
    // yanıtı seçimi görebilsin.
    storeLocale(next, localeStorage);
    void i18n.changeLanguage(next);

    setSyncError(null);
    unwrapEnvelope(
      platformGraph.PUT("/v1/me/locale", { body: { locale: next } }),
    ).then(
      () => {
        setSyncError(null);
      },
      (err: unknown) => {
        setSyncError(err);
      },
    );
  }, []);

  return { locale, setLocale, syncError };
}
